import React, { useState, FC, ReactNode } from 'react';
import Icon from '~ComponentsImproved/Icon';
import ArrowDownIcon from '~Assets/icons/expand_more.svg';
import styled from '@emotion/styled';

const AccordionWrapper = styled.div<{ dark: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ dark }) => (dark ? 'var(--colorBlueGray1000)' : 'var(--colorBlueGray200)')};
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  background: ${({ dark }) => (dark ? 'transparent' : 'var(--colorWhite)')};
`;

const AccordionTitle = styled.div<{ dark: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  font-weight: 600;
  cursor: pointer;
  color: ${({ dark }) => (dark ? 'var(--colorWhite)' : 'var(--colorBlack)')};
`;

const IconWrapper = styled.div<{ isOpen: boolean }>`
  transform: rotate(${({ isOpen }) => (isOpen ? '180deg' : '0deg')});
  transition: transform 0.3s ease;
`;

const AccordionContent = styled.div<{ isOpen: boolean; dark: boolean; preventMaxHeight: boolean }>`
  padding: ${({ isOpen }) => (isOpen ? '16px' : '0 16px')};
  max-height: ${({ isOpen, preventMaxHeight }) =>
    !isOpen ? '0' : preventMaxHeight ? 'none' : '500px'};
  overflow: ${({ preventMaxHeight }) => (preventMaxHeight ? 'visible' : 'auto')};
  transition: ${({ preventMaxHeight }) =>
    preventMaxHeight ? 'padding 0.3s ease' : 'max-height 0.3s ease, padding 0.3s ease'};
  color: ${({ dark }) => (dark ? 'var(--colorWhite)' : 'var(--colorBlack)')};
`;

interface Props {
  /** Title of the accordion - for more flexibility we include ReactNode */
  title: string | ReactNode;
  /** This is default to true - terminology in other components is dark aswell, therefore the naming */
  dark?: boolean;
  /** When true, will prevent the accordion from having a max-height and internal scrolling */
  preventMaxHeight?: boolean;
  children?: ReactNode;
  initiallyOpen?: boolean;
}

const Accordion: FC<Props> = ({
  title,
  dark = true,
  preventMaxHeight = false,
  children,
  initiallyOpen,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(initiallyOpen ?? false);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  return (
    <AccordionWrapper dark={dark}>
      <AccordionTitle dark={dark} onClick={toggleAccordion}>
        {title}
        <IconWrapper isOpen={isOpen}>
          <Icon
            src={ArrowDownIcon}
            width="40px"
            height="40px"
            color={dark ? 'var(--colorWhite)' : 'var(--colorBlack)'}
          />
        </IconWrapper>
      </AccordionTitle>
      <AccordionContent dark={dark} isOpen={isOpen} preventMaxHeight={preventMaxHeight}>
        {children}
      </AccordionContent>
    </AccordionWrapper>
  );
};

export default Accordion;
